import { handleError } from "@/api/handleError";
import { getReceiverLineList } from "@/api/requests/receiverLine";
import { decryptData, generateKeyPair } from "@/helpers/encryptionKeys";
import { fetchSipInfo } from "@/helpers/queries";
import { useBusinessStore } from "@/stores/businesses.store";
import { useNumberStore } from "@/stores/numbers.store";
import { useUsersStore } from "@/stores/users.store";
import type { PressOneSipCredentialInstance } from "@pressone/dialer/lib/types";
import { computed, ref } from "vue";

export function useSipCredentials() {
  const isFetching = ref(false);
  const error = ref<string | null>(null);

  const userStore = useUsersStore();
  const businessStore = useBusinessStore();

  const fetchCredentials = async (): Promise<PressOneSipCredentialInstance> => {
    isFetching.value = true;
    error.value = null;

    try {
      const businessId = businessStore.currentBusinessId;

      if (!businessId) {
        throw new Error("Business ID is required");
      }

      const keypair = await generateKeyPair();
      const publickey = keypair.publicKey;

      const newPayload = {
        public_key: publickey,
        is_no_trunk_team: false,
      };

      const getNewInfo = await getReceiverLineList({
        receiverLineInSchema: newPayload,
        businessId,
      });

      const fetchedSipInfo = await Promise.all(
        getNewInfo.data.map(async (creds) => {
          return {
            ...creds,
            password: await decryptData(keypair.privateKey, creds.password),
            business_id: businessId,
            extension: creds.extension.toString(),
            username: creds.username.toString(),
            host: creds.host?.toString() || "",
            domain: creds.domain?.toString() || "",
            port: creds.port?.toString(),
          };
        })
      );

      // Convert array to object format expected by PressOne
      const result: PressOneSipCredentialInstance = {};
      fetchedSipInfo.forEach((cred, index) => {
        result[index.toString()] = cred;
      });

      return result;
    } catch (err) {
      error.value = "Failed to fetch SIP credentials";
      handleError({ error: err, silent: true });
      throw err;
    } finally {
      isFetching.value = false;
    }
  };

  const fetchCredentialsForNumber = async (
    numberId: string | number
  ): Promise<PressOneSipCredentialInstance> => {
    isFetching.value = true;
    error.value = null;

    try {
      const keypair = await generateKeyPair();
      const publickey = keypair.publicKey;

      const payload = {
        public_key: publickey,
        receiver: numberId,
        mobile: userStore?.currentUserBusinessLevel?.mobile,
      };

      const data = await fetchSipInfo(payload);
      const decryptedPassword = await decryptData(
        keypair.privateKey,
        data.data.password
      );

      const credentials: PressOneSipCredentialInstance = {
        [numberId.toString()]: {
          ...data.data,
          password: decryptedPassword,
        },
      };

      return credentials;
    } catch (err) {
      error.value = "Failed to fetch SIP credentials for number";
      handleError({ error: err, silent: true });
      throw err;
    } finally {
      isFetching.value = false;
    }
  };

  const mapCredentialsToNumbers = (
    credentials: PressOneSipCredentialInstance,
    _numbers: unknown[]
  ): PressOneSipCredentialInstance => {
    const numberStore = useNumberStore();
    const result: PressOneSipCredentialInstance = {};

    if (!numberStore.numbers) {
      return result;
    }

    // Map credentials to business numbers
    for (const number of numberStore.numbers) {
      for (const [, sipCreds] of Object.entries(credentials)) {
        if (
          number?.line?.domain === sipCreds.domain &&
          number?.line?.username.toString() === sipCreds.extension.toString() &&
          number.id &&
          number.business_number?.id
        ) {
          result[number.id] = {
            password: sipCreds.password,
            extension: sipCreds.extension,
            username: sipCreds.username,
            host: sipCreds.host,
            domain: sipCreds.domain,
            port: sipCreds.port,
            protocol: sipCreds.protocol,
            business_id: number.business_number?.id,
          };
        }
      }
    }

    // Add any remaining credentials (like live widget credentials)
    const mappedCredentials = Object.values(result);
    const copiedCredentials = { ...credentials };

    for (const [, sipCreds] of Object.entries(copiedCredentials)) {
      const findMapped = mappedCredentials.find(
        (credential) => credential?.password === sipCreds?.password
      );

      if (!findMapped) {
        const randomId = Math.floor(Math.random() * 1000) + 1;
        result[randomId] = {
          password: sipCreds.password,
          extension: sipCreds.extension,
          username: sipCreds.username,
          host: sipCreds.host,
          domain: sipCreds.domain,
          port: sipCreds.port,
          protocol: sipCreds.protocol,
          business_id: 999, // Special ID for live widget
        };
      }
    }

    return result;
  };

  return {
    isFetching: computed(() => isFetching.value),
    error: computed(() => error.value),
    fetchCredentials,
    fetchCredentialsForNumber,
    mapCredentialsToNumbers,
  };
}
